import {
  DAY, ROUTES, WEEK_HOURS_CAP, categoryKeyFor, classDate, enrolled, isActive, num, readiness, scheduleOf, scheduleProgress, seats, weekBounds, weeklyHoursByInstructor,
} from "../shared/classData";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CAT_COLORS = {
  fire: "var(--color-cat-firefighter)",
  ems: "var(--color-cat-ems)",
  aha: "var(--color-cat-aha)",
  res: "var(--color-cat-rescue)",
};

const short = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const clock = (d) => d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
const colorFor = (c) => CAT_COLORS[categoryKeyFor(c.category, c.title || c.name)];

export function greeting(now) {
  const h = now.getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

/** Monday of the week containing `now`. */
export function weekStart(now) {
  return weekBounds(now).start;
}

/** Mon-Sun calendar built from each class's `schedule` entries (no per-course session fetch needed). */
function buildWeek(classes, now) {
  const start = weekStart(now);
  const today = startOfDay(now);
  return DAY_NAMES.map((name, i) => {
    const date = new Date(+start + i * DAY);
    const sessions = classes
      .filter((c) => c.publishStatus !== "DRAFT")
      .flatMap((c) => scheduleOf(c).map((s) => ({ c, s, a: new Date(s.start), b: new Date(s.end) })))
      .filter(({ a }) => +startOfDay(a) === +date)
      .sort((x, y) => x.a - y.a)
      .map(({ c, s, a, b }) => ({
        id: `${c.id}-${s.start}`,
        courseId: c.id,
        time: clock(a),
        color: colorFor(c),
        name: c.title || c.name || "Session",
        meta: [s.location, c.instructorName].filter(Boolean).join(" · "),
        live: +date === +today && a <= now && now < b,
      }));
    return { name, day: date.getDate(), today: +date === +today, past: date < today, sessions };
  });
}

function buildInstructorLoad(instructors, classes, now) {
  const hours = weeklyHoursByInstructor(classes, now);
  return instructors.map((i) => {
    const id = i.id || i.userId;
    return { id, n: i.fullName || i.name || i.username || "Instructor", h: Math.round(hours[id] || 0) };
  }).sort((a, b) => b.h - a.h).slice(0, 6);
}

function cohortRow(c, now, go) {
  const d = classDate(c);
  const base = { id: c.id, name: c.title || c.name || "Cohort", code: c.courseNumber || "", color: colorFor(c), instructor: c.instructorName || "Unassigned", learners: enrolled(c), cap: seats(c), onClick: () => go(ROUTES.courseHub(c.id)) };
  const status = readiness(c, now);
  if (d && d > now) return { ...base, status: status === "ok" ? "soon" : status, upcoming: true, startLabel: short(d) };
  const prog = scheduleProgress(c, now);
  return { ...base, status, pct: prog ? prog.pct : null, sessionLabel: prog ? `${prog.done}/${prog.total} sessions` : "" };
}

function buildSetup(courses, instructors, go) {
  const done = [instructors.length > 0, courses.length > 0, courses.length > 0, courses.some((c) => enrolled(c) > 0)];
  const nextIdx = done.findIndex((x) => !x);
  const defs = [
    ["Add instructors", `${instructors.length} instructor${instructors.length === 1 ? "" : "s"} on staff`, "Add instructors", ROUTES.instructors],
    ["Add a course", "Pick from the AHA, NREMT or State Fire Marshal catalog, or create your own.", "Add course", ROUTES.manageCourses],
    ["Open a cohort", "Set dates, seats and a lead instructor.", "Open cohort", ROUTES.manageCourses],
    ["Enroll learners", "Invite learners or share the public enrollment link.", "Enroll", ROUTES.catalog],
  ];
  return defs.map(([title, detail, cta, path], i) => ({
    title, detail, cta, state: done[i] ? "done" : i === nextIdx ? "next" : "lock", onClick: () => go(path),
  }));
}

/** Pure view-model builder for CoordinatorDashboard; takes already-fetched data (see classData.js for the class payload). */
export function buildCoordinatorModel({ loading = false, courses = [], submissions = [], instructors = [], expiringCerts = [], stats = {}, user, tenantConfig, navigate = () => {}, now = new Date() }) {
  const active = courses.filter((c) => isActive(c, now));
  const week = buildWeek(courses, now);
  const todayCount = week.find((d) => d.today)?.sessions.length || 0;
  const load = buildInstructorLoad(instructors, active, now);
  const pending = submissions.filter((s) => String(s.status || "").toLowerCase() === "pending");
  const soon = expiringCerts.filter((c) => new Date(c.expires) <= new Date(+now + 30 * DAY)).length;
  const unassigned = active.filter((c) => !c.instructorId && !c.leadInstructorId);
  const over = load.filter((r) => r.h > WEEK_HOURS_CAP);
  const signoff = num(stats?.pendingApprovals);
  const actions = [
    soon && { id: "certs", tone: "w", icon: "cert", title: `${soon} instructor certification${soon === 1 ? "" : "s"} expire in 30 days`, detail: "Review before they lapse", cta: "Review", onClick: () => navigate(ROUTES.instructors) },
    signoff > 0 && { id: "signoff", tone: "w", icon: "clip", title: `${signoff} skill sheet${signoff === 1 ? "" : "s"} awaiting sign-off`, detail: "Across all cohorts", cta: "Open", onClick: () => navigate(ROUTES.catalog) },
    pending.length && { id: "pending", tone: "w", icon: "clip", title: `${pending.length} course submission${pending.length === 1 ? "" : "s"} awaiting review`, detail: "Course approvals", cta: "Review", onClick: () => navigate(ROUTES.approvals) },
    ...over.map((r) => ({ id: `over-${r.id}`, tone: "v", icon: "user", title: `${r.n} is over scheduled hours`, detail: `${r.h} of ${WEEK_HOURS_CAP} this week`, cta: "Reassign", onClick: () => navigate(ROUTES.instructors) })),
    ...unassigned.map((c) => ({ id: `un-${c.id}`, tone: "r", icon: "alert", title: `${c.title || "Cohort"} has no instructor`, detail: "Assign before the start date", cta: "Assign", onClick: () => navigate(ROUTES.courseHub(c.id)) })),
  ].filter(Boolean);
  const learners = active.reduce((s, c) => s + enrolled(c), 0);
  const startsLater = active.filter((c) => classDate(c) > now);
  const nextStart = [...startsLater].sort((a, b) => classDate(a) - classDate(b))[0];
  const checklist = stats?.checklistStats || {};
  const graded = num(checklist.total);
  const first = (user?.fullName || user?.name || user?.username?.split("@")[0] || "there").split(" ")[0];
  const empty = courses.length === 0;
  const certsByWeek = Array.from({ length: 8 }, (_, i) => expiringCerts.filter((c) => {
    const e = new Date(c.expires);
    return e >= new Date(+now + i * 7 * DAY) && e < new Date(+now + (i + 1) * 7 * DAY);
  }).length);
  const tenantName = tenantConfig?.name || "Training Center";
  const monday = weekStart(now);
  const byStart = [...active].sort((a, b) => classDate(a) - classDate(b));
  return {
    loading,
    empty,
    tenantName,
    header: {
      eyebrow: `${tenantName} · ${now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`,
      greeting: `${greeting(now)}, ${first}`,
      sessionsToday: todayCount,
      itemsNeedAttention: actions.length,
    },
    kpis: {
      enrollment: learners,
      cohorts: active.length,
      cohortMini: byStart.slice(0, 5).map(enrolled),
      nextStart: nextStart ? short(classDate(nextStart)) : null,
      startsLater: startsLater.length,
      certs: expiringCerts.length,
      certsSoon: soon,
      certsSpark: certsByWeek,
      passRate: graded > 0 && checklist.passRate != null ? Number(checklist.passRate) : null,
      graded,
    },
    week: { label: `${short(monday)} – ${short(new Date(+monday + 6 * DAY))}`, days: week },
    actions,
    cohorts: byStart.slice(0, 5).map((c) => cohortRow(c, now, navigate)),
    setup: buildSetup(courses, instructors, navigate),
    load: load.length ? load : instructors.slice(0, 3).map((i) => ({ id: i.id || i.userId, n: i.fullName || i.username || "Instructor", h: 0 })),
    navigate,
  };
}
